import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import {
  buildChartPerformanceFetchKey,
  formatChartPerformanceDate,
  normalizeChartPerformancePayload,
} from "../../lib/utils/chartPerformanceReport"

/**
 * GET /api/v1/report/page/clients/{clientId}/chart/performance?startDate=&endDate=
 */
export const fetchClientChartPerformance = createAsyncThunk(
  "clientChartPerformance/fetch",
  async ({ clientId, startDate, endDate } = {}, { rejectWithValue }) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }
      if (!startDate || !endDate) {
        return rejectWithValue("startDate and endDate are required")
      }

      const start = formatChartPerformanceDate(startDate)
      const end = formatChartPerformanceDate(endDate)
      const query = new URLSearchParams({ startDate: start, endDate: end }).toString()
      const data = await get(`report/page/clients/${clientId}/chart/performance?${query}`)
      const normalized = normalizeChartPerformancePayload(data)

      return {
        platforms: normalized.platforms,
        trendSeries: normalized.trendSeries,
        clientId: String(clientId),
        startDate: start,
        endDate: end,
        fetchKey: buildChartPerformanceFetchKey(clientId, start, end),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch chart performance")
    }
  },
)

const initialState = {
  platforms: [],
  trendSeries: [],
  status: "idle",
  error: null,
  requestedFetchKey: null,
  fetchedFetchKey: null,
  clientId: null,
  startDate: null,
  endDate: null,
}

function beginFetch(state, action) {
  const { clientId, startDate, endDate } = action.meta.arg || {}
  const start = formatChartPerformanceDate(startDate)
  const end = formatChartPerformanceDate(endDate)
  const nextKey = buildChartPerformanceFetchKey(clientId, start, end)
  const keepStale =
    nextKey === state.fetchedFetchKey &&
    Array.isArray(state.trendSeries) &&
    state.trendSeries.length > 0

  state.status = "loading"
  state.error = null
  state.requestedFetchKey = nextKey
  if (!keepStale) {
    state.fetchedFetchKey = null
    state.platforms = []
    state.trendSeries = []
  }
}

function isStaleFetch(action, state) {
  const { clientId, startDate, endDate } = action.meta.arg || {}
  const start = formatChartPerformanceDate(startDate)
  const end = formatChartPerformanceDate(endDate)
  const key = buildChartPerformanceFetchKey(clientId, start, end)
  return key !== String(state.requestedFetchKey ?? "")
}

const clientChartPerformanceSlice = createSlice({
  name: "clientChartPerformance",
  initialState,
  reducers: {
    clearClientChartPerformance: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientChartPerformance.pending, (state, action) => {
        beginFetch(state, action)
      })
      .addCase(fetchClientChartPerformance.fulfilled, (state, action) => {
        if (isStaleFetch(action, state)) return
        state.status = "succeeded"
        state.platforms = action.payload?.platforms ?? []
        state.trendSeries = action.payload?.trendSeries ?? []
        state.clientId = action.payload?.clientId ?? null
        state.startDate = action.payload?.startDate ?? null
        state.endDate = action.payload?.endDate ?? null
        state.fetchedFetchKey = action.payload?.fetchKey ?? null
      })
      .addCase(fetchClientChartPerformance.rejected, (state, action) => {
        if (isStaleFetch(action, state)) return
        state.status = "failed"
        state.error = action.payload ?? "Failed to fetch chart performance"
        state.platforms = []
        state.trendSeries = []
        state.fetchedFetchKey = state.requestedFetchKey
      })
  },
})

export const { clearClientChartPerformance } = clientChartPerformanceSlice.actions
export default clientChartPerformanceSlice.reducer
